import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Ebook } from '@/types/types';
import { supabase } from '@/lib/supabase';

interface EbookContextType {
  ebooks: Ebook[];
  favoriteEbooks: Ebook[];
  isLoading: boolean;
  error: string | null;
  toggleFavorite: (id: number) => void;
  getEbooksByCategory: (category: string) => Ebook[];
  isCategoryLoading: boolean;
}

const EbookContext = createContext<EbookContextType | undefined>(undefined);

export const useEbooks = () => {
  const context = useContext(EbookContext);
  if (context === undefined) {
    throw new Error('useEbooks must be used within an EbookProvider');
  }
  return context;
};

interface EbookProviderProps {
  children: ReactNode;
}

export const EbookProvider: React.FC<EbookProviderProps> = ({ children }) => {
  const [ebooks, setEbooks] = useState<Ebook[]>([]);
  const [favoriteEbooks, setFavoriteEbooks] = useState<Ebook[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCategoryLoading, setIsCategoryLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch ebooks from Supabase
  useEffect(() => {
    const fetchEbooks = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Replace with your actual table name
        const { data, error } = await supabase.from("ebooks").select("*");
        
        if (error) throw new Error(error.message);
        
        // Transform data if needed
        const transformedData = data.map(item => ({
          id: item.id,
          title: item.title,
          author: item.author,
          cover: item.cover,
          category: item.category,
          rating: item.rating,
          pages: item.pages,
          reviews: item.reviews,
          isFavorite: false,
          description: item.description
        }));
        
        setEbooks(transformedData);
        
        // Load favorites from localStorage
        const savedFavorites = localStorage.getItem('favoriteEbooks');
        if (savedFavorites) {
          const favoriteIds = JSON.parse(savedFavorites);
          const favorites = transformedData.filter(book => favoriteIds.includes(book.id));
          setFavoriteEbooks(favorites);
          
          // Update isFavorite flag
          setEbooks(transformedData.map(book => ({
            ...book,
            isFavorite: favoriteIds.includes(book.id)
          })));
        }
      } catch (err) {
        console.error('Error fetching ebooks:', err);
        setError('Failed to load ebooks. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEbooks();
  }, []);

  // Toggle favorite status
  const toggleFavorite = (id: number) => {
    // Update ebooks
    setEbooks(prev => 
      prev.map(book => 
        book.id === id ? { ...book, isFavorite: !book.isFavorite } : book
      )
    );
    
    // Update favoriteEbooks
    const book = ebooks.find(book => book.id === id);
    if (!book) return;
    
    if (book.isFavorite) {
      // Remove from favorites
      setFavoriteEbooks(prev => prev.filter(book => book.id !== id));
    } else {
      // Add to favorites
      setFavoriteEbooks(prev => [...prev, { ...book, isFavorite: true }]);
    }
    
    // Save to localStorage
    const updatedFavorites = book.isFavorite 
      ? favoriteEbooks.filter(book => book.id !== id).map(book => book.id)
      : [...favoriteEbooks.map(book => book.id), id];
    
    localStorage.setItem('favoriteEbooks', JSON.stringify(updatedFavorites));
  };

  // Get ebooks by category
  const getEbooksByCategory = (category: string) => {
    setIsCategoryLoading(true);
    
    // Small delay to show loading state
    setTimeout(() => {
      setIsCategoryLoading(false);
    }, 300);
    
    if (category === "Toutes") {
      return ebooks;
    }
    
    return ebooks.filter(book => book.category === category);
  };

  return (
    <EbookContext.Provider 
      value={{ 
        ebooks, 
        favoriteEbooks, 
        isLoading, 
        error, 
        toggleFavorite, 
        getEbooksByCategory,
        isCategoryLoading
      }}
    >
      {children}
    </EbookContext.Provider>
  );
};